<template>
  <div>
    <el-form
      :model="withdrawForm"
      ref="withdrawForm"
      :rules="withdrawFormRules"
      class="top_left"
      :class="{ 'left-style': showRight }"
    >
      <el-form-item>
        <div class="title">
          <span>{{ $t('withdraw.default.accountDetail') }}</span>
        </div>
      </el-form-item>
      <el-form-item class="labelTitle">
        <div>
          <label for="">{{ $t('withdraw.default.tradingPlatform') }}</label>
        </div>
        <div class="row">{{ mtAccountType }}</div>
      </el-form-item>
      <div class="accSelect">
        <SelectForm
          v-model="withdrawForm.formAccount"
          :label="$t('withdraw.default.accountSelect')"
          name="formAccount"
          class="showPlaceholder"
        >
          <el-option
            v-for="item in formAccountOptions"
            :key="item.login"
            :value="JSON.stringify(item)"
            :label="getCurrencySymbol(item)"
            :data-testid="item.login"
            ><template v-if="participatedTCAccount.includes(item.login)">
              {{ getCurrencySymbol(item) }}
              <span class="ml-2"><TrophyIcon /></span>
            </template>
          </el-option>
        </SelectForm>
      </div>
      <div
        v-if="isShowWithdrawalAmountNote"
        class="flex flex-col items-stretch justify-start gap-3.5 -mt-[15px] mb-[45px]"
      >
        <div v-if="isShowWithdrawalAmountInfoText" class="p-4 rounded-lg bg-cyanG0 text-redError whitespace-pre-line">
          {{ $t('withdraw.default.withdrawalAmountInfoText') }}
        </div>
        <div v-if="socialTrading.hasFollow" class="p-4 rounded-lg bg-cyanG0 text-redError">
          <div class="mb-2.5 font-bold">
            <i class="el-icon-warning" />&nbsp;{{ $t('social.trading.performance.fee.importantNote.label') }}
          </div>
          {{ $t('social.trading.performance.fee.description', { performanceFee: socialTrading.performanceFee }) }}
          <br /><br />
          {{ $t('social.trading.performance.fee.actualFeeFormula', { performanceFee: socialTrading.performanceFee }) }}
          <br /><br />
          {{ $t('social.trading.performance.fee.note') }}
        </div>
      </div>
      <div class="amount">
        <numeric-input
          ref="numberInput"
          v-model="withdrawForm.amount"
          :currency="withdrawForm.currency"
          :transaction="'withdrawal'"
          :precision="2"
          :label="$t('withdraw.default.withdrawalAmount')"
          name="amount"
        ></numeric-input>
      </div>
      <div class="tips mb-10 border border-solid border-slate-300 px-3.5 py-6" v-if="isShowTips">
        <p class="leading-5">
          <span class="text-red">*</span>
          <i18n path="withdraw.tips.desc1" tag="span">
            <template v-slot:fundLimit>
              <span class="text-blueG0">{{ tips.totalLimitedAmount }}&nbsp;{{ tips.currency }}</span>
            </template>
            <template v-slot:tradeVol>
              <span class="text-blueG0">{{ tips.totalTradeLotRequired }}</span>
            </template>
          </i18n>
        </p>
        <i18n path="withdraw.tips.desc2" tag="p" class="leading-5 mt-2.5">
          <template v-slot:account>
            <span class="font-bold">{{ tips.currentUnlockMt4AccountId }}</span>
          </template>
          <template v-slot:unlockedAmount>
            <span class="text-blueG0">{{ tips.currentUnlockAmount }}&nbsp;{{ tips.currentUnlockCurrency }}</span>
          </template>
          <template v-slot:remainLots>
            <span class="text-blueG0">{{ tips.currentTradeLotRequired }}</span>
          </template>
        </i18n>
      </div>
      <div class="form_button">
        <el-button class="blue_button" @click="submit" data-testid="submit">{{
          $t('common.button.continue')
        }}</el-button>
      </div>
    </el-form>
    <CampaignWithdrawalModal
      :campaignWithdrawalModalVisible.sync="campaignWithdrawalModalVisible"
      :mt4account="withdrawForm.account"
      @proceed="withdrawConfirmProceed"
    >
    </CampaignWithdrawalModal>
  </div>
</template>

<script>
import NumericInput from '@/components/NumericInput';
import splitThousands from '@/util/splitThousands';
import uscMixin from '@/mixins/usc';
import promotionMixin from '@/mixins/promotion';
import {
  apiGetWithdrawalData_cp,
  apiCreditCardBalances,
  apiCheckClientTradeLot,
  apiGetLBTWithdrawalData_cp
} from '@/resource';
import { decimalAdding } from '@/util/decimal';
import { countryCodeEnum } from '@/constants/country';
import { COUNTRY_CREDIT_CARD_BLACKLIST } from '@/constants/creditCardTransfer';
import CampaignWithdrawalModal from './CampaignWithdrawalModal';
import WithdrawalFreezeDialog from '@/components/withdraw/WithdrawalFreezeDialog.vue';
import TrophyIcon from '@/components/icon/Trophy.vue';
import { countryIsoCode } from '@/constants/countryCode';

export default {
  name: 'TopLeft',
  components: { NumericInput, CampaignWithdrawalModal, TrophyIcon, WithdrawalFreezeDialog },
  mixins: [uscMixin, promotionMixin],
  props: {
    // {isSuccess:false, hasFollow: false, performanceFee: 0}
    socialTrading: { type: Object, required: true },
    checkSocialTradingFollower: { type: Function, required: true }
  },
  data() {
    const validateAmount = (rule, value, callback) => {
      if (value === '' || !Number(value)) {
        callback(new Error(this.$t('common.formValidation.amtReq')));
      } else if (parseFloat(value) <= 0) {
        callback(new Error(this.$t('common.formValidation.amt0')));
      } else if (value < this.minLimit) {
        const performanceFeeError = this.socialTrading.hasFollow
          ? ` (${this.$t('social.trading.performance.fee.label')}: $${this.socialTrading.performanceFee})`
          : '';

        callback(
          new Error(
            `${this.$t('common.formValidation.amtLarger', {
              minLimit: this.minLimit
            })} ${this.withdrawForm.currency}` + performanceFeeError
          )
        );
      } else if (
        value > this.withdrawForm.balance ||
        (this.tips.totalLimitedAmount != '' && value > this.tips.totalLimitedAmount)
      ) {
        callback(new Error(this.$t('withdraw.default.YourPaymentGreaterThanAvailableBalance')));
      } else if (this.maxLimit && value > this.maxLimit) {
        callback(
          new Error(
            this.$t('common.formValidation.amtLess', { maxLimit: this.maxLimit, currency: this.withdrawForm.currency })
          )
        );
      } else callback();
    };
    return {
      campaignWithdrawalModalVisible: false,
      creditCardBalances: [],
      lbtTypesAndBalancesData: [],
      formAccountOptions: [],
      countryBankList: [],
      userCountryCode: null,
      mtAccountType: null,
      withdrawForm: {
        formAccount: '',
        account: '',
        amount: 0,
        mtCategory: '',
        currency: null,
        balance: null
      },
      methodMinAmt: 0,
      disabled: false,
      withdrawFormRules: {
        formAccount: [
          {
            required: true,
            message: this.$t('common.formValidation.accReq'),
            trigger: 'change'
          }
        ],
        amount: [
          {
            required: true,
            validator: validateAmount,
            trigger: 'blur'
          }
        ]
      },
      topForm: {},
      reset: true,
      showRight: false,
      clickedProceed: false,
      chooseWithdrawalTypeId: null,
      isShowTips: false,
      tips: {
        currency: '',
        totalLimitedAmount: '',
        totalTradeLotRequired: '',
        currentUnlockMt4AccountId: '',
        currentUnlockAmount: '',
        currentUnlockCurrency: '',
        currentTradeLotRequired: ''
      }
    };
  },
  mounted() {
    this.fetchTradingAccount();
    this.$root.$refs.topLeft = this;
  },
  computed: {
    countryCode() {
      return parseInt(this.$store.state.common.countryCode);
    },
    isShowWithdrawalAmountNote() {
      return this.isShowWithdrawalAmountInfoText || this.socialTrading.hasFollow;
    },
    isShowWithdrawalAmountInfoText() {
      return [countryCodeEnum.MALAYSIA, countryCodeEnum.VIETNAM].includes(this.countryCode);
    },
    minLimit() {
      let minWithdrawalAmount = 0;
      switch (true) {
        case this.withdrawForm.currency === 'EUR':
          minWithdrawalAmount = 35;
          break;
        case this.withdrawForm.currency === 'GBP':
          minWithdrawalAmount = 30;
          break;
        case this.withdrawForm.currency === 'JPY':
          minWithdrawalAmount = 3000;
          break;
        case this.withdrawForm.currency === 'HKD':
          minWithdrawalAmount = 300;
          break;
        case this.isInterac():
          minWithdrawalAmount = 50;
          break;
        case this.checkIsUSC(this.withdrawForm.currency):
          minWithdrawalAmount =
            this.methodMinAmt > 0 ? this.convertUSDToUSC(this.methodMinAmt) : this.convertUSDToUSC(40);
          break;
        default:
          minWithdrawalAmount = this.methodMinAmt > 0 ? this.methodMinAmt : 40;
      }

      const minLimit =
        Number(minWithdrawalAmount) + (this.socialTrading.hasFollow ? this.socialTrading.performanceFee : 0);

      // Avoid floating-point arithmetic issue
      return parseFloat(minLimit.toFixed(2));
    },
    maxLimit() {
      if (this.isInterac()) return 9999.99;
      return null;
    },
    showCC() {
      return this.creditCardBalances.length > 0;
    },
    showLBT() {
      return (
        this.withdrawForm.amount > this.totalCcBalance &&
        this.lbtBalance > 0 &&
        this.lbtTypesAndBalancesData.isLbtLimited
      );
    },
    showOtherPayment() {
      return (
        this.withdrawForm.amount > decimalAdding(this.totalCcBalance, this.lbtBalance, 2) ||
        (this.userCountryCode === countryCodeEnum.NETHERLANDS_ANTILLES && this.withdrawForm.amount > this.lbtBalance)
      );
    },
    totalCcBalance() {
      let total = 0;
      this.creditCardBalances.forEach(cc => {
        if (cc.creditCard.is_del !== 1 && !cc.isExpired && !cc.isPending) {
          total = decimalAdding(total, cc.balance, 2);
        }
      });
      return total;
    },
    lbtBalance() {
      const hasProperty = this.lbtTypesAndBalancesData.lbtBalance.hasOwnProperty(this.withdrawForm.currency);
      const isLimited = this.lbtTypesAndBalancesData.isLbtLimited;

      if (hasProperty && isLimited) {
        return this.lbtTypesAndBalancesData.lbtBalance[this.withdrawForm.currency];
      } else {
        return 0;
      }
    },
    toShowCampaignWithdrawalModal() {
      if (this.clickedProceed) return false;

      const isAccountParticipatedTC = this.participatedTC.find(campaign => {
        return (
          campaign.participantAccount?.some(account => account.mt4Account === this.withdrawForm.account) &&
          campaign.withdrawDisqualify === true
        );
      });

      return isAccountParticipatedTC;
    },
    getCountryIsoCode() {
      return countryIsoCode.find(c => c.id === parseInt(this.userCountryCode)).code;
    }
  },
  watch: {
    withdrawForm: {
      handler(form) {
        this.$emit('setForm', form);
        this.$emit('reset', true);
        this.reset = true;
      },
      deep: true
    },
    'withdrawForm.formAccount'(value) {
      this.clickedProceed = false;
      const jsonParse = JSON.parse(value);

      apiCheckClientTradeLot({ userId: jsonParse.userId, accountId: jsonParse.login }).then(res => {
        if (res?.data?.data && !res.data.data.noWithdrawLimitRecord) {
          const {
            currency,
            totalLimitedAmount,
            totalTradeLotRequired,
            currentUnlockMt4AccountId,
            currentUnlockAmount,
            currentUnlockCurrency,
            currentTradeLotRequired
          } = res.data.data;

          this.tips = {
            ...this.tips,
            currency,
            totalLimitedAmount,
            totalTradeLotRequired,
            currentUnlockMt4AccountId,
            currentUnlockAmount,
            currentUnlockCurrency,
            currentTradeLotRequired
          };
          this.isShowTips = true;
        } else {
          this.isShowTips = false;
        }
      });

      this.withdrawForm.account = jsonParse.login;
      this.withdrawForm.balance = jsonParse.balance;
      this.withdrawForm.mtCategory = jsonParse.mtCategory;
      if (this.withdrawForm.currency === jsonParse.currency) {
        this.$refs.numberInput.updateCurrency(true);
      }
      this.withdrawForm.currency = jsonParse.currency;

      this.methodMinAmt = 0;
      this.$refs['withdrawForm'].fields.find(f => f.prop == 'amount').resetField();

      this.mtAccountType = jsonParse.mtAccountTypeDisplay;

      this.disabled = false;
    },
    'withdrawForm.amount'(value) {
      // this.methodMinAmt = 0;
      this.disabled = false;
    },
    formAccountOptions(options) {
      if (options.length > 0) {
        this.withdrawForm.formAccount = JSON.stringify(options[0]);
      }
    }
  },
  methods: {
    fetchTradingAccount() {
      apiGetWithdrawalData_cp()
        .then(resp => {
          if (resp.data.code === 0) {
            this.userCountryCode = parseInt(resp.data.data.countryCode);
            this.formAccountOptions = resp.data.data.logins;

            this.countryBankList = new Map(
              resp.data.data.withdrawalType.map(witdrawType => {
                let withdrawBankList = witdrawType.withdrawBankList;
                let bankList = [];
                for (const key in withdrawBankList) {
                  if (Object.hasOwnProperty.call(withdrawBankList, key)) {
                    const bank = withdrawBankList[key];
                    bankList.push({ label: bank, value: bank });
                  }
                }

                bankList.sort((a, b) => a.label.localeCompare(b.label));
                return [witdrawType.id, bankList];
              })
            );
          }
        })
        .catch(err => {
          this.$message({
            message: this.$t('withdraw.default.fetchTradeAccFailed'),
            type: 'error'
          });
        });
    },
    withdrawConfirmProceed(val) {
      if (val == true) {
        this.clickedProceed = true;
        this.submit();
      }
    },
    submit() {
      if (this.toShowCampaignWithdrawalModal) {
        this.campaignWithdrawalModalVisible = true;
        return;
      }

      this.$refs['withdrawForm'].validate(async valid => {
        if (valid) {
          if (!this.socialTrading.isSuccess) {
            // Retry to checkSocialTradingFollower
            await this.checkSocialTradingFollower();
          }

          if (this.socialTrading.isSuccess) {
            // Validate again since minLimit updated
            this.$refs['withdrawForm'].validate(valid => {
              if (valid) {
                this.getApiBalances();
              }
            });
          }
        } else return false;
      });
    },
    checkMinLimit(amount, typeId) {
      this.methodMinAmt = amount;
      this.chooseWithdrawalTypeId = typeId;
      this.$refs['withdrawForm'].validate();
    },
    getApiBalances() {
      Promise.all([
        apiCreditCardBalances({ currency: this.withdrawForm.currency }),
        apiGetLBTWithdrawalData_cp({ country: this.getCountryIsoCode })
      ])
        .then(resp => {
          this.creditCardBalances = resp[0].data;
          this.lbtTypesAndBalancesData = resp[1].data.data;
          this.showRight = true;

          this.emitTopForm();
          this.$emit('reset', false);
        })
        .catch(err => {
          console.log(err);
        });
    },
    emitTopForm() {
      this.topForm = {
        creditCardBalances: this.creditCardBalances,
        showCC: this.showCC,
        showOtherPayment: this.showOtherPayment,
        totalCcBalance: this.totalCcBalance,
        userCountryCode: this.userCountryCode,
        lbtTypesAndBalancesData: this.lbtTypesAndBalancesData,
        lbtBalance: this.lbtBalance,
        showLBT: this.showLBT
      };

      this.$emit('setTopForm', this.topForm, this.countryBankList, this.showRight);
      this.disabled = this.showCC || this.showLBT || this.showOtherPayment;
    },
    getCurrencySymbol(item) {
      return (
        item.login +
        ' - ' +
        item.accountDisplayType +
        ' - ' +
        this.$options.filters.currencyToSymbol(item.currency) +
        splitThousands(item.balance, 2) +
        ' ' +
        item.currency
      );
    },
    resetWithdrawalData() {
      this.$emit('reset', true);
      this.getApiBalances();
    },
    isInterac() {
      return this.withdrawForm?.currency === 'CAD' && this.chooseWithdrawalTypeId == 99;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/assets/css/components/withdraw/topLeft.scss';
</style>
